// src/portal-app/more/SettingsScreen.js
//
// Plan 4 Task 21 — Settings screen rewrite (P3 — grouped list).
//
// Was: 6 GlassCard "sections" (icon badge + title) each rendering a fixed
// list of label/value rows, falling back to a bare em-dash ('—') per unset
// field. A tenant with several genuinely-unset config fields (no theme
// color, no service charge, no stations configured, ...) could show a run
// of ~13 dash rows across the screen — read by the user as complaint #7,
// "Settings isn't showing the actual data?" Spec §1 is explicit this was
// never a data bug: those fields ARE unset for that tenant, and the fix is
// presentational, not a fetch/binding fix (none was made — every field
// below reads the exact same config path as before).
//
// Now (P3 — spec §5: "a standard iOS grouped list: sectioned rows with
// label left, optional right-hand value, chevron. No hero, no filters, no
// cards-in-a-grid"):
//   SectionHead   — plain uppercase text label per section (no icon badge —
//                   that was P4/P5 card-header styling; a P3 grouped list
//                   uses plain section headers, same as EmployeeDetailScreen
//                   / ProductDetailScreen's SectionHead, and this screen has
//                   no per-row navigation so GroupedRows' chevron slot is
//                   simply unused here, not removed from the contract).
//   GroupedRows   — the SET fields in a section, as label/value rows.
//   EmptyFieldsPrompt — every run of consecutive unset fields in a section
//                   collapses into ONE prompt naming them. Never one dash
//                   row per missing field — that collapse is the whole
//                   point of this task. All-unset section -> prompt only,
//                   no empty GroupedRows card above it. None-unset -> no
//                   prompt.
//   PullToRefresh — spec §8; wraps the scrollable body incl. the desktop
//                   banner, re-running the same load() as SurfaceStates'
//                   retry.
//
// Read-only contract UNCHANGED (spec §3: "Settings remains read-only in the
// app — that decision from W5 stands and is correct"): no mutations, no
// edit buttons, no save calls, no form inputs anywhere in this file.
// EmptyFieldsPrompt supports an onAdd Add-button affordance, but there is no
// edit sheet on this screen for it to open (same reasoning as
// insights/ProductDetailScreen.js's FieldBlock) — wiring one here would
// dangle an affordance this read-only screen cannot deliver on, which is a
// worse bug than the dashes this task fixes. onAdd is never passed.
//
// This screen renders CONTENT ONLY — NavStackHost owns the header.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { tenantService } from '../../services/api';
import { SurfaceStates } from '../insights/kit';
import {
    GroupedRows, EmptyFieldsPrompt, PullToRefresh, ensureKitCss,
} from '../shell/kit';
import { useNavChrome } from '../nav/NavStack';

/* ------------------------------------------------------------------ */
/* Screen-local stylesheet                                             */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- layout ---- */
.stg-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;
  padding:8px 0 80px;}

/* ---- desktop banner ---- */
.stg-banner{
  margin:0 16px 16px;padding:14px 16px;border-radius:16px;
  display:flex;align-items:flex-start;gap:12px;
  background:color-mix(in srgb,var(--c-blue) 8%,transparent);
  border:1px solid color-mix(in srgb,var(--c-blue) 20%,transparent);}
.stg-banner-icon{flex-shrink:0;margin-top:1px;}
.stg-banner-text{display:flex;flex-direction:column;gap:2px;}
.stg-banner-title{font-size:13px;font-weight:700;color:var(--c-blue);}
.stg-banner-sub{font-size:12px;color:var(--c-textLight);line-height:1.4;}

/* ---- section header (title only — P3 grouped list, no icon badge,      */
/* no edit action: this screen is read-only and has nothing to trigger) --*/
.stg-shead{padding-inline:16px;margin-block:18px 8px;}
.stg-shead-title{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:.8px;color:var(--c-textLight);}
.stg-block{padding-inline:16px;margin-block-end:12px;}
.stg-block+.stg-block{margin-block-start:-4px;}

/* ---- color swatch (Theme Color row icon) ---- */
.stg-swatch{width:20px;height:20px;border-radius:6px;flex-shrink:0;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 12%,transparent);}

/* ---- logo image sizing (used inside .stg-logo-preview below) ---- */
.stg-logo{max-height:32px;max-width:110px;object-fit:contain;border-radius:6px;}

/* ---- receipt preview (Business section extra — not a collapse field:   */
/* freeform optional text, same "legitimately absent" treatment           */
/* EmployeeDetailScreen gives End Date/Termination Reason) ---- */
.stg-receipt{margin-inline:16px;margin-block:0 12px;padding:10px 12px;border-radius:10px;
  background:color-mix(in srgb,var(--c-surface) 30%,transparent);
  font-size:11px;color:var(--c-textLight);line-height:1.6;font-style:italic;}
.stg-receipt-label{font-size:10px;font-weight:700;text-transform:uppercase;
  letter-spacing:.7px;color:var(--c-textLight);margin-bottom:4px;
  font-style:normal;}
.stg-divider{height:1px;background:color-mix(in srgb,var(--c-surface) 40%,transparent);
  margin:10px 0;}

/* ---- logo preview (Appearance section extra — see file header: the      */
/* actual <img> renders here, OUTSIDE GroupedRows, following the same      */
/* pattern as the receipt preview above; the Logo row's "value" in         */
/* GroupedRows is a plain string so its "title" attribute stays honest) ---- */
.stg-logo-preview{margin-inline:16px;margin-block:0 12px;padding:10px 12px;
  border-radius:10px;background:color-mix(in srgb,var(--c-surface) 30%,transparent);
  display:flex;align-items:center;}

/* ---- a logo that will not load (Notion item 11). Read-only screen, so    */
/* this says where the fix lives rather than offering one. It is styled as  */
/* an alert and not as muted helper text on purpose: a tenant whose         */
/* branding stopped printing at the rebrand went months without knowing,    */
/* because every surface hid the failure with onerror. ---- */
.stg-logo-failed{font-size:12px;line-height:1.5;color:var(--c-red);}
`;

let _cssInjected = false;
function ensureCss() {
    if (_cssInjected) return;
    const el = document.createElement('style');
    el.textContent = CSS;
    document.head.appendChild(el);
    _cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* Inline SVG icon — banner only. The six per-section icon badges from    */
/* the old GlassCard headers (IconBuilding/IconSales/IconTeam/IconPayroll/ */
/* IconAppearance/IconEod) are dropped along with those headers — a P3     */
/* grouped list uses plain text section labels (see file header), and the */
/* icons carried no information (all aria-hidden decoration).             */
/* ------------------------------------------------------------------ */
const IconInfo = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        aria-hidden="true">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="16" x2="12" y2="12"/>
        <line x1="12" y1="8" x2="12.01" y2="8"/>
    </svg>
);

/* ------------------------------------------------------------------ */
/* Sub-components                                                       */
/* ------------------------------------------------------------------ */

/** Plain section label — see file header for why there's no icon/edit slot. */
const SectionHead = ({ title }) => (
    <div className="stg-shead">
        <span className="stg-shead-title">{title}</span>
    </div>
);

/* The collapse rule (spec §1 / this task's whole point). Partitions
   `fields` into set/unset. Set ones render as GroupedRows; every unset one
   collapses into ONE EmptyFieldsPrompt naming them — never one dash row per
   missing field. All-unset -> prompt only, no empty card above it.
   None-unset -> no prompt. No onAdd — see file header (read-only screen,
   no edit sheet for the prompt's Add button to open). */
const FieldBlock = ({ fields }) => {
    const set = fields.filter((f) => f.value);
    const unset = fields.filter((f) => !f.value);
    const rows = set.map((f) => ({
        id: f.key, label: f.label, value: f.value, icon: f.icon,
    }));

    if (rows.length === 0 && unset.length === 0) return null;

    return (
        <div className="stg-block">
            {rows.length > 0 && <GroupedRows rows={rows} />}
            {unset.length > 0 && (
                <EmptyFieldsPrompt fields={unset.map((f) => f.label)} />
            )}
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* Main screen                                                          */
/* ------------------------------------------------------------------ */

export default function SettingsScreen() {
    ensureCss();
    ensureKitCss();

    const { t, isRTL } = useLanguage();
    // useNavChrome is called for consistency with other screens (no trailing
    // actions needed here — read-only screen has nothing to put in the header)
    useNavChrome(null);

    const [status, setStatus] = useState('loading');
    const [config, setConfig] = useState(null);
    const [logoFailed, setLogoFailed] = useState(false);

    // Stale-response guard — same pattern as team/EmployeeDetailScreen.js
    // and insights/ProductDetailScreen.js: bump seqRef before each fetch,
    // drop any response whose sequence no longer matches the latest.
    const seqRef = useRef(0);
    const load = useCallback(() => {
        seqRef.current += 1;
        const seq = seqRef.current;
        setStatus('loading');
        // Returned so PullToRefresh can await it — its spinner stays up
        // until the fetch actually settles, success or failure.
        return tenantService.getTenantConfig()
            .then((data) => {
                if (seq !== seqRef.current) return;
                setConfig(data);
                setStatus('ready');
            })
            .catch(() => {
                if (seq !== seqRef.current) return;
                setStatus('error');
            });
    }, []);

    useEffect(() => { load(); }, [load]);

    /* ---- derived values (all defensive — unchanged field bindings) ---- */
    const paymentMethods = Array.isArray(config?.payment_methods) ? config.payment_methods : [];
    const diningOptions = Array.isArray(config?.dining_options) ? config.dining_options : [];
    const stations = Array.isArray(config?.stations) ? config.stations : [];
    const departments = Array.isArray(config?.departments) ? config.departments : [];
    const dailyCats = Array.isArray(config?.daily_expense_categories)
        ? config.daily_expense_categories
        : [];
    const ohCats = Array.isArray(config?.overhead_expense_categories)
        ? config.overhead_expense_categories
        : [];

    const receiptHeader = config?.receipt_header || null;
    const receiptFooter = config?.receipt_footer || null;
    const themeColor = config?.theme?.primary_color || null;
    // `receipt_logo` / `brand_logo` are what the uploaders write — the web
    // Portal's and the superadmin one alike. This used to read
    // `config.theme.logo_url`, a key NOTHING writes and NO tenant has, so the
    // preview below had never rendered for anybody. Notion item 11.
    const logoUrl = config?.receipt_logo || config?.brand_logo || null;
    const eodMode = config?.eod_mode || null;

    // A new address is a fresh chance to succeed. Without this, a tenant who
    // fixes their logo in the Portal and pulls to refresh here keeps being
    // told it is broken.
    useEffect(() => { setLogoFailed(false); }, [logoUrl]);

    /* ---- eod label ---- */
    const eodLabel = eodMode === 'per_store'
        ? t('per_store', {}, 'Per Store')
        : eodMode === 'per_till'
            ? t('per_till', {}, 'Per Till')
            : eodMode;

    /* ---- field lists, one per FieldBlock ---- */
    const businessFields = [
        { key: 'brand', label: t('brand_name_label', {}, 'Brand Name'), value: config?.brand_name || null },
        { key: 'currency', label: t('currency_label', {}, 'Currency'), value: config?.currency || null },
        { key: 'timezone', label: t('timezone_label', {}, 'Timezone'), value: config?.timezone || null },
        {
            key: 'business_type',
            label: t('business_type_label', {}, 'Business Type'),
            value: config?.business_type || null,
        },
    ];

    const salesFields = [
        {
            key: 'payment_methods',
            label: t('payment_methods_label', {}, 'Payment Methods'),
            value: paymentMethods.length > 0
                ? `${paymentMethods.length} (${paymentMethods.join(', ')})`
                : null,
        },
        {
            key: 'dining_options',
            label: t('dining_options_label', {}, 'Dining Options'),
            value: diningOptions.length > 0 ? String(diningOptions.length) : null,
        },
        {
            key: 'service_charge',
            label: t('service_charge_label', {}, 'Service Charge'),
            value: config?.service_charge_rate != null
                ? `${Math.round(config.service_charge_rate * 100)}%`
                : null,
        },
    ];

    const teamFields = [
        {
            key: 'stations',
            label: t('stations_label', {}, 'Stations'),
            value: stations.length > 0 ? stations.join(', ') : null,
        },
        {
            key: 'departments',
            label: t('departments_label', {}, 'Departments'),
            value: departments.length > 0 ? departments.join(', ') : null,
        },
    ];
    const expenseFields = [
        {
            key: 'daily_expenses',
            label: t('expense_categories_label', {}, 'Daily Expenses'),
            value: dailyCats.length > 0 ? dailyCats.join(', ') : null,
        },
        {
            key: 'overhead_expenses',
            label: t('overhead_categories_label', {}, 'Overhead Expenses'),
            value: ohCats.length > 0 ? ohCats.join(', ') : null,
        },
    ];

    const payrollFields = [
        { key: 'tip_policy', label: t('tip_policy_label', {}, 'Tip Policy'), value: config?.tip_policy || null },
    ];

    const appearanceFields = [
        {
            key: 'theme_color',
            label: t('theme_color_label', {}, 'Theme Color'),
            value: themeColor,
            // Deliberate data-display exception: renders the tenant's
            // actual primary_color as a swatch icon — not a design-token
            // violation, and never the sole carrier of meaning since the
            // hex value itself is also the row's text value.
            icon: themeColor
                ? <span className="stg-swatch" style={{ background: themeColor }} title={themeColor} />
                : undefined,
        },
        {
            // GroupedRows sets `title={String(row.value)}` on the value span
            // unconditionally (GroupedRows.js) — passing the <img> itself as
            // `value` made that title read the literal string
            // "[object Object]" on the branding row, which undercuts the
            // whole point of this task (stop Settings from reading as
            // broken). `value` here is a plain, honest status string
            // ("Uploaded" — not a filename or URL guess, nothing fabricated
            // about the logo itself); the actual image renders as a
            // supplementary block below the FieldBlock, same pattern as the
            // Business section's receipt preview.
            key: 'logo',
            label: t('logo_label', {}, 'Logo'),
            value: logoUrl
                ? (logoFailed
                    ? t('logo_not_loading', {}, 'Not loading')
                    : t('logo_uploaded', {}, 'Uploaded'))
                : null,
        },
    ];

    const eodFields = [
        { key: 'eod_mode', label: t('eod_mode_label', {}, 'EOD Mode'), value: eodLabel || null },
    ];

    return (
        <div
            className="stg-wrap"
            dir={isRTL ? 'rtl' : 'ltr'}
            data-testid="settings-screen"
        >
            <PullToRefresh onRefresh={load}>
                {/* "Manage from desktop" banner — informational, not list
                    content, so it renders OUTSIDE SurfaceStates and stays
                    visible through loading/error, not just the ready state. */}
                <div
                    className="stg-banner"
                    data-testid="settings-desktop-banner"
                    role="note"
                    aria-label={t('manage_settings_desktop', {}, 'Manage settings from desktop')}
                >
                    <span className="stg-banner-icon">
                        <IconInfo />
                    </span>
                    <div className="stg-banner-text">
                        <span className="stg-banner-title">
                            {t('manage_settings_desktop', {}, 'Manage settings from desktop')}
                        </span>
                        <span className="stg-banner-sub">
                            {t('manage_settings_desktop_sub', {},
                                'This is a read-only view. To change settings, open the Manager Portal on a desktop browser.')}
                        </span>
                    </div>
                </div>

                <SurfaceStates status={status} onRetry={load}>
                    {config && (
                        <div>

                            {/* ── 1. Business ── */}
                            <div data-testid="settings-business-card">
                                <SectionHead title={t('settings_business', {}, 'Business')} />
                                <FieldBlock fields={businessFields} />
                                {(receiptHeader || receiptFooter) && (
                                    <div className="stg-receipt">
                                        {receiptHeader && (
                                            <>
                                                <div className="stg-receipt-label">
                                                    {t('receipt_header_label', {}, 'Header')}
                                                </div>
                                                {receiptHeader}
                                            </>
                                        )}
                                        {receiptHeader && receiptFooter && (
                                            <div className="stg-divider" />
                                        )}
                                        {receiptFooter && (
                                            <>
                                                <div className="stg-receipt-label">
                                                    {t('receipt_footer_label', {}, 'Footer')}
                                                </div>
                                                {receiptFooter}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* ── 2. Sales ── */}
                            <div data-testid="settings-sales-card">
                                <SectionHead title={t('settings_sales', {}, 'Sales')} />
                                <FieldBlock fields={salesFields} />
                            </div>

                            {/* ── 3. Team & Expenses ── */}
                            <div data-testid="settings-team-card">
                                <SectionHead title={t('settings_team_expenses', {}, 'Team & Expenses')} />
                                <FieldBlock fields={teamFields} />
                                <FieldBlock fields={expenseFields} />
                            </div>

                            {/* ── 4. Payroll ── */}
                            <div data-testid="settings-payroll-card">
                                <SectionHead title={t('settings_payroll', {}, 'Payroll')} />
                                <FieldBlock fields={payrollFields} />
                            </div>

                            {/* ── 5. Appearance ── */}
                            <div data-testid="settings-appearance-card">
                                <SectionHead title={t('settings_appearance', {}, 'Appearance')} />
                                <FieldBlock fields={appearanceFields} />
                                {logoUrl && (
                                    <div className="stg-logo-preview">
                                        <img
                                            data-testid="settings-logo"
                                            src={logoUrl}
                                            alt={t('logo_alt', {}, 'Brand logo')}
                                            className="stg-logo"
                                            onError={() => setLogoFailed(true)}
                                            style={logoFailed ? { display: 'none' } : undefined}
                                        />
                                        {logoFailed && (
                                            // The point of item 11. Hiding the
                                            // failure made a broken logo look
                                            // identical to no logo, which is
                                            // how six of seven tenants went
                                            // months without knowing theirs
                                            // had stopped printing.
                                            <div
                                                data-testid="settings-logo-failed"
                                                role="alert"
                                                className="stg-logo-failed"
                                            >
                                                {t(
                                                    'logo_failed_to_load_native',
                                                    {},
                                                    'Your logo could not be loaded, so it is not printing on receipts. Upload it again from Settings in the Portal on a computer.',
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* ── 6. EOD ── */}
                            <div data-testid="settings-eod-card">
                                <SectionHead title={t('settings_eod', {}, 'End of Day')} />
                                <FieldBlock fields={eodFields} />
                            </div>

                        </div>
                    )}
                </SurfaceStates>
            </PullToRefresh>
        </div>
    );
}
